import { useCallback } from 'react'

import type { Row } from '@tanstack/react-table'
import { useUnit } from 'effector-react'

import EditIcon from '@/assets/icons/icon-edit.svg'
import RefreshIcon from '@/assets/icons/icon-refresh.svg'
import RemoveIcon from '@/assets/icons/icon-trash.svg'
import { Tooltip } from '@/components/ui/tooltip'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $currentCreator } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $funnelCheckInProgressIds } from '@/modules/tracking/funnel/models.ts'

import { useFunnelChecksModifier } from '../../hooks/use-funnel-checks-modifier'
import { type FunnelCheck } from '../../types'

interface Props {
  row: Row<FunnelCheck>
}

export const FunnelTrackingTableActions = ({ row }: Props) => {
  const activeCreator = useUnit($currentCreator)
  const checkInProgressIds = useUnit($funnelCheckInProgressIds)
  const { manualCheck, editCheck, deleteCheck } = useFunnelChecksModifier()
  const disabled = checkInProgressIds.includes(row.original.id)

  const handleOpenDeleteDialog = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.FUNNEL_TRACKING_DELETE,
      onSubmit: () => deleteCheck.mutateAsync({ checkId: row.original.id }),
    })
  }, [deleteCheck, row.original.id])

  const handleManualCheck = useCallback(
    () => manualCheck.mutateAsync({ checkId: row.original.id }),
    [manualCheck, row.original.id],
  )

  const handleOpenEditDialog = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.FUNNEL_TRACKING_EDIT,
      item: row.original,
      creatorName: activeCreator?.name ?? '',
      onSubmit: (body) =>
        editCheck.mutateAsync({ checkId: row.original.id, body }),
    })
  }, [activeCreator?.name, editCheck, row.original])

  return (
    <div className="float-right flex gap-4">
      <button
        type="button"
        disabled={disabled}
      >
        <EditIcon
          width={18}
          height={18}
          className="text-primary-500 hover:text-primary-800"
          onClick={handleOpenEditDialog}
        >
          Edit
        </EditIcon>
      </button>

      <button
        type="button"
        disabled={disabled}
      >
        <RemoveIcon
          width={18}
          height={18}
          onClick={handleOpenDeleteDialog}
          className="text-danger-default hover:text-danger-dark"
        >
          Remove
        </RemoveIcon>
      </button>
      <Tooltip
        content="Manual check"
        trigger={
          <button
            type="button"
            onClick={handleManualCheck}
            disabled={disabled}
          >
            <RefreshIcon
              width={18}
              height={18}
              className="text-primary-500 hover:text-primary-800"
            >
              Refresh
            </RefreshIcon>
          </button>
        }
      />
    </div>
  )
}
